import React, { Fragment, useState, useEffect } from "react";
import { useUpdater } from "@td/shared_utils";
import { authToken } from "@td/api";
import { mapKeysDeep, transformKey, transformKeyUnderscore } from "@td/utils";

const initialFormState = { note: "", disposition: "", alert: "N" };

const NurseNotes = ({
  consultationId,
  dispositionOptions,
  hasLabResults,
  notes: initialNotes
}) => {
  const [notes, setNotes] = useState(initialNotes);
  const [formState, setFormState] = useState(initialFormState);
  const [noteRequired, setNoteRequired] = useState(true);
  const [errorMessage, setErrorMessage] = useState(null);
  const [successMessage, setSuccessMessage] = useState(null);

  // api call to add note
  const { callApi, data, loading, error } = useUpdater("post", {
    url: `v1/consultations/${consultationId}/add_rx_additional_notes`,
    method: "post",
    headers: {
      "Content-Type": "application/json",
      Authorization: `${authToken.get()}`
    }
  });

  // misc assignments
  const newlyAddedNote = data && data.data;

  // update form state when input changes
  const handleInputChange = event => {
    if (event.persist) event.persist();
    setFormState(formState => ({
      ...formState,
      [event.target.name]: event.target.value
    }));

    // clear out the messages
    setErrorMessage(_ => null);
    setSuccessMessage(_ => null);
  };

  // when disposition changes, update related state
  useEffect(() => {
    if (formState.disposition) {
      // Find the selected option
      const option = dispositionOptions.find(
        ({ text }) => formState.disposition == text
      );
      const { alert, explanationRequired } = option || {
        alert: false,
        explanationRequired: true
      };

      // Update the alert flag in form state
      setFormState(formState => ({
        ...formState,
        alert: alert ? "Y" : "N"
      }));

      // Update the note required flag
      setNoteRequired(explanationRequired);
    }
  }, [formState.disposition]);

  // validate form data
  const validate = () => {
    // form is very simple, could use formik if it gets more complicated
    if (noteRequired && !formState.note) {
      setErrorMessage("Note cannot be blank");
      return false;
    }
    setErrorMessage(null);
    return true;
  };

  // on form submission, make api call to save note
  const handleSubmit = event => {
    if (event) event.preventDefault();
    if (validate()) {
      // convert camel case to underscores
      let payload = mapKeysDeep(formState, transformKeyUnderscore);

      // If no note text entered, it defaults to the selected disposition
      if (!payload.note) {
        payload = { ...payload, note: formState.disposition };
      }
      callApi(payload);
    }
  };

  // when api call to save note fails, display error message
  useEffect(() => {
    if (error) {
      setErrorMessage(
        "Note could not be saved, please reload the page and try again."
      );
    }
  }, [error]);

  // when api call to save note successeds, add new note to the list
  useEffect(() => {
    if (newlyAddedNote) {
      setFormState(initialFormState);
      setNotes(notes => [mapKeysDeep(newlyAddedNote, transformKey), ...notes]);
      setSuccessMessage(_ => "Note Saved!");
      setNoteRequired(_ => true);
    }
  }, [newlyAddedNote]);

  return (
    <Fragment>
      {/* List of existing notes */}
      <div>
        <ul className="notes">
          {notes.length === 0 ? (
            <li className="no_notes">
              <em>No notes entered</em>
            </li>
          ) : (
            notes.map(note => (
              <Fragment key={note.clinicalNoteDt}>
                <li>
                  <p>{note.note}</p>
                  <strong>{note.authorName}</strong>
                  <br />
                  <em>
                    <span className="browser_timestamp">
                      {note.clinicalNoteDt}
                    </span>
                  </em>
                </li>
                <br />
              </Fragment>
            ))
          )}
        </ul>
      </div>
      <br />

      {/* Form to add note */}
      <form id="rx_additional_notes">
        {errorMessage && (
          <p className="further-explanation-error" style={{ color: "#f00" }}>
            {errorMessage}
          </p>
        )}
        {newlyAddedNote && (
          <p className="success" style={{ color: "#0b0" }}>
            {successMessage}
          </p>
        )}

        {/* Dropdown to select disposition */}
        {hasLabResults && (
          <Fragment>
            <div>
              <label htmlFor="disposition_select">
                Lab results disposition code (optional):
              </label>
              <br />
              <select
                id="disposition_select"
                name="disposition"
                value={formState.disposition}
                onChange={handleInputChange}
              >
                <option value=""></option>
                {dispositionOptions.map(({ text }) => (
                  <option key={text} value={text}>
                    {text}
                  </option>
                ))}
              </select>
            </div>
            <br />
          </Fragment>
        )}

        {/* Text area for the note */}
        <div>
          <label htmlFor="disposition_select">
            Enter Note{!noteRequired && " (optional)"}:
          </label>
          <br />
          <textarea
            name="note"
            value={formState.note}
            onChange={handleInputChange}
          ></textarea>
        </div>
        <br />

        {/* Submit button */}
        <div>
          <input
            name="commit"
            type="submit"
            value="Save"
            disabled={loading}
            className={loading ? "disabled" : ""}
            onClick={handleSubmit}
          />
        </div>
      </form>
      <br />
    </Fragment>
  );
};

export { NurseNotes };